import { format } from "date-fns";
import { Bone, Check, PawPrint, Trash2, X } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-sand text-ink border-line",
  approved: "bg-forest/10 text-forest border-forest/25",
  declined: "bg-red-50 text-red-700 border-red-200",
};

const Bones = ({ value }) => (
  <div className="flex gap-1" aria-label={`${value} out of 5 bones`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Bone
        key={n}
        className={`h-4 w-4 ${n <= value ? "text-marmalade" : "text-line"}`}
        fill={n <= value ? "currentColor" : "none"}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const AdminReviews = ({ reviews, onStatus, onDelete }) => (
  <div
    className="mt-8 overflow-x-auto rounded-3xl border border-line bg-white shadow-[0_8px_30px_rgba(42,42,42,0.04)]"
    data-testid="admin-reviews-table"
  >
    {reviews.length === 0 ? (
      <div className="flex flex-col items-center gap-4 px-6 py-20 text-center" data-testid="admin-reviews-empty">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sand">
          <PawPrint className="h-6 w-6 text-marmalade" />
        </span>
        <p className="font-display text-2xl text-ink">No reviews yet</p>
        <p className="max-w-xs text-sm text-inkSoft">
          Reviews customers submit on the website will land here for approval.
        </p>
      </div>
    ) : (
      <table className="w-full min-w-[800px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft">
            <th className="px-6 py-4">From</th>
            <th className="px-4 py-4">Paws</th>
            <th className="px-4 py-4">Review</th>
            <th className="px-4 py-4">Status</th>
            <th className="px-6 py-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((r) => (
            <tr
              key={r.id}
              data-testid={`admin-review-row-${r.id}`}
              className="border-b border-line transition-colors duration-200 last:border-b-0 hover:bg-sand/50"
            >
              <td className="px-6 py-5 text-ink">
                {r.owner_name}
                <span className="mt-1 block text-xs text-inkSoft">with {r.pet_name}</span>
              </td>
              <td className="px-4 py-5">
                <Bones value={r.rating} />
              </td>
              <td className="max-w-[420px] px-4 py-5 text-ink">
                <span className="block leading-relaxed">“{r.text}”</span>
                <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-inkSoft">
                  {format(new Date(r.created_at), "MMM d, h:mm a")}
                </span>
              </td>
              <td className="px-4 py-5">
                <span
                  className={`inline-flex rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLES[r.status]}`}
                  data-testid={`review-status-${r.status}`}
                >
                  {r.status}
                </span>
              </td>
              <td className="px-6 py-5">
                {r.status === "pending" ? (
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onStatus(r.id, "approved")}
                      data-testid={`admin-review-approve-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-forest px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-cream transition-[background-color] duration-200 hover:bg-ink"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Approve
                    </button>
                    <button
                      onClick={() => onStatus(r.id, "declined")}
                      data-testid={`admin-review-decline-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-red-700 transition-colors duration-200 hover:bg-red-50"
                    >
                      <X className="h-3.5 w-3.5" />
                      Decline
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft">
                      {format(new Date(r.created_at), "MMM d, h:mm a")}
                    </span>
                    <button
                      onClick={() => onDelete(r.id)}
                      data-testid={`admin-review-delete-${r.id}`}
                      aria-label="Delete review"
                      className="rounded-full border border-red-200 bg-white p-2 text-red-600 transition-colors duration-200 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default AdminReviews;
